import { useQuery } from '@tanstack/react-query';
import '../OrderSummaryCard.scss';
import CheckboxWithoutLabel from '@trackman/web-shared-components/components/Checkbox/CheckboxWithoutLabel';
import Subtitle from 'components/TextComps/Subtitle';
import { ReactComponent as ShoppingCart } from 'images/icons/ShoppingCart.svg';
import { ReactComponent as Trash } from 'images/icons/Trash.svg';
import { useServices, useStores } from 'index';

import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DefaultRoutes } from 'Routes';
import { useCartStore } from 'stores/Webshop/CartStore';
import { CartItem } from 'types/cart';
import { removeCartItemFromLocalStorage } from 'utilities/cartLocalStorageHelper';

export const OrderSummaryCardInteractive = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notificationsStore = useStores().notificationStore;

  const profileService = useServices().profileService;
  const { data: isPremium, isPending } = useQuery({
    queryKey: ['isUserMember'],
    queryFn: profileService.checkIfUserIsMember,
    refetchOnWindowFocus: false,
  });

  const removeItemFromCart = useCartStore((state) => state.removeItemFromCart);
  const handleAgreeToTermsAndConditions = useCartStore((state) => state.setAgreedToTermsAndConditions);
  const itemsInCart: CartItem[] = useCartStore((state) => state.items);
  const agreedToTermsAndConditions = useCartStore((state) => state.agreedToTermsAndConditions);

  const certificationsInCart = itemsInCart.filter((item) => item.id !== 'premium');
  const hasDiscount = (!isPending && isPremium) || itemsInCart.some((item) => item.id === 'premium');

  const totalPrice = itemsInCart.reduce(
    (total, item) => (hasDiscount && item.id !== 'premium' ? total + item.price * 0.5 : total + item.price),
    0
  );

  const handleRemovingEntryFromCart = (item: CartItem) => {
    removeItemFromCart(item.id);
    removeCartItemFromLocalStorage(item.id);
    notificationsStore.addToast(
      t('RemovedFromCart', { ns: 'webshop' }),
      t('CertificateHasBeenRemovedFromCart', { ns: 'webshop', certificateName: item.title }),
      'info'
    );
  };

  return (
    <div className='order-summary-card'>
      {itemsInCart.length > 0 ? (
        <>
          <Subtitle>
            <>{t('OrderSummary', { ns: 'webshop' })}</>
          </Subtitle>
          <div className='items-list'>
            {itemsInCart.find((item) => item.id === 'premium') && (
              <SubscriptionSummaryEntry
                key='premium'
                subscription={itemsInCart.find((item) => item.id === 'premium')!}
                removeItemFromCart={handleRemovingEntryFromCart}
              />
            )}
            {certificationsInCart.map((item) => (
              <CertificationSummaryEntry
                key={item.id}
                certification={item}
                hasDiscount={hasDiscount}
                removeItemFromCart={handleRemovingEntryFromCart}
              />
            ))}
          </div>
          <div className='subtotal-row'>
            <span className='title'>{t('SubtotalExcludingVAT', { ns: 'webshop' })}</span>
            <span className='price'>${totalPrice}</span>
          </div>
          <div className='purchase-disclaimer'>
            <span>{t('PurchaseDisclaimer', { ns: 'webshop' })}</span>
            <div>
              <CheckboxWithoutLabel
                onChange={(e) => handleAgreeToTermsAndConditions(e.target.checked)}
                checked={agreedToTermsAndConditions}
              />
              <Trans i18nKey='TermsAndConditions' ns='webshop'>
                I agree to the&nbsp;
                <span onClick={() => navigate(DefaultRoutes.TermsAndConditions)} className='terms-and-conditions-link'>
                  Terms and conditions
                </span>
                &nbsp;.
              </Trans>
            </div>
          </div>
        </>
      ) : (
        <span className='empty-cart'>
          <ShoppingCart /> {t('YourCartIsEmpty', { ns: 'webshop' })}
        </span>
      )}
    </div>
  );
};

const SubscriptionSummaryEntry = ({
  subscription,
  removeItemFromCart,
}: {
  subscription: CartItem;
  removeItemFromCart: (item: CartItem) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className='summary-row'>
      <Trash onClick={() => removeItemFromCart(subscription)} />
      <div className='title title__subscription'>
        <span>{subscription.title}</span>
        <span>/ {t('Annually', { ns: 'webshop' })}</span>
      </div>
      <span className='price'>${subscription.price}</span>
    </div>
  );
};

const CertificationSummaryEntry = ({
  certification,
  hasDiscount,
  removeItemFromCart,
}: {
  certification: CartItem;
  hasDiscount: boolean;
  removeItemFromCart: (item: CartItem) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className='summary-row'>
      <Trash onClick={() => removeItemFromCart(certification)} />
      <span className='title title__certification'>{t('Certification', { ns: 'webshop', certificationName: certification.title })}</span>
      <span className='price'>
        <span className={hasDiscount ? 'base-price base-price_discounted' : 'base-price'}>${certification.price}</span>
        {hasDiscount && <span className='final-price'>${certification.price * 0.5}</span>}
      </span>
    </div>
  );
};
